import { Schema } from 'effect';

import { IsoDateTimeUtc } from '@finnest/core/effect';

import { ReleaseVersion } from '../../_shared/release-version.ts';
import { CustomerId, RunId } from '../vocabulary/ids/branded-ids.ts';
import { jsonValueSchema } from '../vocabulary/json-value.ts';

// Canonical field-shape maps for the evidence run detail response.
//
// These maps are the single source of truth for the evidence run shape.
// @finnest/power-onboarding's evidence writer builds its on-disk envelope/event
// schemas from the same maps (it already depends on this package). admin-contracts
// is a leaf contract and must not depend back on the domain, so the shape lives
// here and the domain consumes it, building its own Schema.Struct from the maps.

export const evidenceRunEnvelopeShape = {
  schemaVersion: Schema.Literal('power-onboarding.evidence-run.v1'),
  runId: RunId,
  phaseDir: Schema.NonEmptyString,
  customerId: CustomerId,
  actorId: Schema.NonEmptyString,
  startedAt: IsoDateTimeUtc,
  /** The release that opened the run; absent on runs recorded before releases were. */
  release: Schema.optionalKey(ReleaseVersion),
  finishedAt: Schema.optionalKey(IsoDateTimeUtc),
  exitCriteriaGreen: Schema.optionalKey(Schema.Boolean),
} as const;

export const evidenceRunEventShape = {
  kind: Schema.NonEmptyString,
  payload: jsonValueSchema,
  actorId: Schema.NonEmptyString,
  occurredAt: IsoDateTimeUtc,
} as const;

const evidenceRunEnvelopeSchema = Schema.Struct(evidenceRunEnvelopeShape);

const evidenceRunEventSchema = Schema.Struct(evidenceRunEventShape);

// One file under the run's `actions/<subStep>/` directory. `content` carries
// the UTF-8 text for small text artifacts (.json/.md/.txt within the embed
// budget); binary or oversized artifacts ship `content: null` with only the
// name + size, so the run detail response stays bounded.
const evidenceRunArtifactSchema = Schema.Struct({
  subStep: Schema.NonEmptyString,
  name: Schema.NonEmptyString,
  sizeBytes: Schema.Int.check(Schema.isGreaterThanOrEqualTo(0)),
  content: Schema.NullOr(Schema.NonEmptyString),
});

export const onboardingEvidenceRunSchema = Schema.Struct({
  envelope: evidenceRunEnvelopeSchema,
  events: Schema.Array(evidenceRunEventSchema),
  artifacts: Schema.Array(evidenceRunArtifactSchema),
}).annotate({
  title: 'Power onboarding evidence run detail',
  description: 'Wire shape for one onboarding evidence run: envelope + NDJSON event tail.',
});
export type OnboardingEvidenceRun = typeof onboardingEvidenceRunSchema.Type;
